export type PresenceBadge = "here_tonight" | "here_this_week";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const MAX_INACTIVITY_MS = 16 * 24 * 60 * 60 * 1000;

function toEpochMs(value: Date | string | null | undefined): number | null {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === "string" && value.length > 0) {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/** Within last 24h -> tonight; missing or older -> this week. */
export function getPresenceBadge(enteredAt: Date | string | null | undefined, now: Date = new Date()): PresenceBadge {
  const enteredAtMs = toEpochMs(enteredAt);
  if (enteredAtMs == null) {
    return "here_this_week";
  }
  return now.getTime() - enteredAtMs <= TWENTY_FOUR_HOURS_MS ? "here_tonight" : "here_this_week";
}

/**
 * Weekly-pool opt-out: `includeInWeeklyPool === false` hides the profile from discovery
 * when they would only show as "Here this week". Missing field = opted in.
 */
export function passesWeeklyPoolVisibility(
  user: {
    includeInWeeklyPool?: boolean | undefined;
    lastKnownEventEnteredAt?: Date | string | null | undefined;
  },
  now: Date = new Date()
): boolean {
  if (user.includeInWeeklyPool !== false) {
    return true;
  }
  return getPresenceBadge(user.lastKnownEventEnteredAt, now) === "here_tonight";
}

/**
 * Staleness cut-off: no event entry within 16 days hides the profile from discovery.
 * Falls back to signup date; profiles with neither timestamp stay visible.
 */
export function passesRecentActivityVisibility(
  user: {
    lastKnownEventEnteredAt?: Date | string | null | undefined;
    created_time?: Date | string | null | undefined;
  },
  now: Date = new Date()
): boolean {
  const lastActiveMs = toEpochMs(user.lastKnownEventEnteredAt) ?? toEpochMs(user.created_time);
  if (lastActiveMs == null) {
    return true;
  }
  return now.getTime() - lastActiveMs <= MAX_INACTIVITY_MS;
}

/**
 * Discovery ordering comparator: boosted profiles first, then most recent
 * `lastKnownEventEnteredAt` descending, with missing timestamps last.
 */
export function compareUsersByEventRecency(
  a: { isBoosted?: boolean | undefined; lastKnownEventEnteredAt?: Date | string | null | undefined },
  b: { isBoosted?: boolean | undefined; lastKnownEventEnteredAt?: Date | string | null | undefined }
): number {
  const aBoost = a.isBoosted === true ? 1 : 0;
  const bBoost = b.isBoosted === true ? 1 : 0;
  if (aBoost !== bBoost) {
    return bBoost - aBoost;
  }

  const aMs = toEpochMs(a.lastKnownEventEnteredAt);
  const bMs = toEpochMs(b.lastKnownEventEnteredAt);
  if (aMs == null && bMs == null) {
    return 0;
  }
  if (aMs == null) {
    return 1;
  }
  if (bMs == null) {
    return -1;
  }
  return bMs - aMs;
}
